import React, { useEffect, useState } from 'react';
import { adminGetCoupons, adminCreateCoupon, adminUpdateCoupon, adminDeleteCoupon } from '../../api';
import './AdminLayout.css';

const EMPTY = { code: '', percent: 10, isActive: true, expiresAt: '', maxUses: '' };

// yyyy-mm-dd for <input type="date">
const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');

function statusOf(c) {
  if (!c.isActive) return { label: 'Off', color: '#9ca3af' };
  if (c.expiresAt && new Date(c.expiresAt) < new Date()) return { label: 'Expired', color: '#ef4444' };
  if (c.maxUses && c.usedCount >= c.maxUses) return { label: 'Used up', color: '#f59e0b' };
  return { label: 'Active', color: '#22c55e' };
}

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'new' | coupon object
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminGetCoupons().then(r => { setCoupons(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  function openNew() { setForm(EMPTY); setModal('new'); }
  function openEdit(c) {
    setForm({ code: c.code, percent: c.percent, isActive: c.isActive, expiresAt: toDateInput(c.expiresAt), maxUses: c.maxUses || '' });
    setModal(c);
  }

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }

  async function handleSave() {
    const code = form.code.trim().toUpperCase();
    const percent = Number(form.percent);
    if (!/^[A-Z0-9_-]{3,20}$/.test(code)) return alert('Code must be 3–20 letters, numbers, - or _');
    if (!(percent >= 1 && percent <= 90)) return alert('Discount must be between 1% and 90%');
    const payload = {
      code,
      percent,
      isActive: form.isActive,
      // End of the chosen day, so a coupon "until 31 Oct" works all day on the 31st
      expiresAt: form.expiresAt ? new Date(`${form.expiresAt}T23:59:59`).toISOString() : null,
      maxUses: form.maxUses ? Number(form.maxUses) : null,
    };
    setSaving(true);
    try {
      if (modal === 'new') {
        const { data } = await adminCreateCoupon(payload);
        setCoupons(prev => [data, ...prev]);
      } else {
        const { data } = await adminUpdateCoupon(modal._id, payload);
        setCoupons(prev => prev.map(c => c._id === modal._id ? data : c));
      }
      setModal(null);
    } catch (err) {
      alert(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(c) {
    try {
      const { data } = await adminUpdateCoupon(c._id, { ...c, isActive: !c.isActive });
      setCoupons(prev => prev.map(x => x._id === c._id ? data : x));
    } catch {
      alert('Update failed');
    }
  }

  async function handleDelete(c) {
    if (!window.confirm(`Delete coupon "${c.code}"? Past orders keep their discount.`)) return;
    try {
      await adminDeleteCoupon(c._id);
      setCoupons(prev => prev.filter(x => x._id !== c._id));
    } catch {
      alert('Delete failed');
    }
  }

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Coupons</h1>
          <p className="admin-page-sub">Coupon discounts stack with the automatic 10% off above ₹1,999</p>
        </div>
        <button className="btn-primary" onClick={openNew}>+ Add Coupon</button>
      </div>

      <div style={{ padding: '16px 28px 28px' }}>
        <div className="admin-card">
          {loading ? (
            <div className="admin-loading">Loading…</div>
          ) : coupons.length === 0 ? (
            <div className="empty-state">
              <span>🏷️</span>
              <p>No coupons yet.</p>
              <button className="btn-primary" onClick={openNew}>Create first coupon</button>
            </div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr><th>Code</th><th>Discount</th><th>Status</th><th>Used</th><th>Expires</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {coupons.map(c => {
                    const status = statusOf(c);
                    return (
                      <tr key={c._id}>
                        <td><strong style={{ fontFamily: 'monospace', fontSize: 14 }}>{c.code}</strong></td>
                        <td>{c.percent}% off</td>
                        <td><span style={{ color: status.color, fontWeight: 600 }}>● {status.label}</span></td>
                        <td>{c.usedCount}{c.maxUses ? ` / ${c.maxUses}` : ''}</td>
                        <td style={{ color: '#6b7280', fontSize: 13 }}>
                          {c.expiresAt ? new Date(c.expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Never'}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn-icon" onClick={() => toggleActive(c)}>{c.isActive ? '⏸ Turn off' : '▶ Turn on'}</button>
                            <button className="btn-icon" onClick={() => openEdit(c)}>✏️ Edit</button>
                            <button className="btn-danger" onClick={() => handleDelete(c)}>🗑️</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modal === 'new' ? 'Add Coupon' : 'Edit Coupon'}</h2>
              <button className="modal-close" onClick={() => setModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Code *</label>
                <input value={form.code} onChange={e => set('code', e.target.value.toUpperCase())} placeholder="e.g. WELCOME10" />
                <span className="form-hint">Customers type this at checkout (not case-sensitive)</span>
              </div>
              <div className="form-group">
                <label>Discount % *</label>
                <input type="number" min={1} max={90} value={form.percent} onChange={e => set('percent', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Expires on</label>
                <input type="date" value={form.expiresAt} onChange={e => set('expiresAt', e.target.value)} />
                <span className="form-hint">Leave empty for no expiry</span>
              </div>
              <div className="form-group">
                <label>Usage limit</label>
                <input type="number" min={1} value={form.maxUses} onChange={e => set('maxUses', e.target.value)} placeholder="Unlimited" />
                <span className="form-hint">Total paid orders allowed to use this code. Leave empty for unlimited.</span>
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.isActive} onChange={e => set('isActive', e.target.checked)} style={{ width: 'auto' }} />
                  Active
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : modal === 'new' ? 'Create Coupon' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
